/**
 * Rate Limiter Utility
 * Tracks form submissions by email address with a 24-hour limit per email
 */

const STORAGE_KEY = 'contactFormRateLimitByEmail';
const RATE_LIMIT_HOURS = 24;
const RATE_LIMIT_MS = RATE_LIMIT_HOURS * 60 * 60 * 1000; // 86400000 ms

/**
 * Check if an email has submitted the form within the rate limit period
 * @param email - The email address to check
 * @returns true if rate limit is exceeded, false otherwise
 */
export const isRateLimitExceeded = (email: string): boolean => {
  if (!email) return false;

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    const submissionsMap = storedData ? JSON.parse(storedData) : {};

    const lastSubmissionTime = submissionsMap[email];

    if (!lastSubmissionTime) {
      return false; // No previous submission
    }

    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;

    // Check if still within rate limit period
    return timeSinceLastSubmission < RATE_LIMIT_MS;
  } catch (error) {
    console.error('Error checking rate limit:', error);
    return false;
  }
};

/**
 * Record a successful form submission for an email
 * @param email - The email address that submitted
 */
export const recordSubmission = (email: string): void => {
  if (!email) return;

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    const submissionsMap = storedData ? JSON.parse(storedData) : {};

    // Record the current timestamp for this email
    submissionsMap[email] = Date.now();

    // Clean up old entries (older than 24 hours)
    const now = Date.now();
    for (const [key, timestamp] of Object.entries(submissionsMap)) {
      if (now - (timestamp as number) > RATE_LIMIT_MS) {
        delete submissionsMap[key];
      }
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(submissionsMap));
  } catch (error) {
    console.error('Error recording submission:', error);
  }
};

/**
 * Clear rate limit for a specific email (for testing purposes)
 * @param email - The email to clear
 */
export const clearRateLimitForEmail = (email: string): void => {
  if (!email) return;

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    const submissionsMap = storedData ? JSON.parse(storedData) : {};

    delete submissionsMap[email];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(submissionsMap));
  } catch (error) {
    console.error('Error clearing rate limit:', error);
  }
};

/**
 * Clear all rate limit data (for testing purposes)
 */
export const clearAllRateLimits = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing all rate limits:', error);
  }
};
